import { Injectable, inject } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, throwError, BehaviorSubject } from "rxjs";
import { catchError, filter, take, switchMap } from "rxjs/operators";
import { AuthEffects } from "../State/AuthEffects";
import { StorageEffects } from "../../../Common/Storage/State/StorageEffects";
import { StorageKeys } from "../../../Common/Storage/Dto/StorageKeys";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly authEffects = inject(AuthEffects);
  private readonly storage = inject(StorageEffects);
  private readonly router = inject(Router);

  /**
   * Attaches the current access token to outgoing requests and intercepts
   * unauthorized responses so protected calls can be retried after refresh.
   */
  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const accessToken = this.storage.getItem(StorageKeys.AccessToken);

    if (accessToken && this.shouldAttachAccessToken(req)) {
      req = req.clone({
        setHeaders: { Authorization: `Bearer ${accessToken}` },
      });
    }

    return next.handle(req).pipe(
      catchError((err: unknown) => {
        const isHttpUnauthorized = err instanceof HttpErrorResponse && err.status === 401;
        const isHttpForbidden = err instanceof HttpErrorResponse && err.status === 403;
        const shouldHandleUnauthorized = this.shouldHandleUnauthorized(req);

        if (isHttpUnauthorized && shouldHandleUnauthorized) {
          return this.handle401Error(req, next);
        }

        if (isHttpForbidden && this.shouldNavigateForbidden(req)) {
          void this.router.navigate(["/403"], { queryParams: { returnUrl: this.router.url } });
        }

        return throwError(() => err);
      }),
    );
  }

  /**
   * Performs a single refresh flow for concurrent 401s, then retries the
   * original request with the newly issued access token.
   */
  private handle401Error<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return new Observable((observer) => {
        this.authEffects
          .refreshToken()
          .then(() => {
            const newToken = this.storage.getItem(StorageKeys.AccessToken);
            if (newToken) {
              this.refreshTokenSubject.next(newToken);
              this.isRefreshing = false;

              const newReq = this.addTokenHeader(req, newToken);
              next.handle(newReq).subscribe({
                next: (val) => observer.next(val),
                error: (err) => observer.error(err),
                complete: () => observer.complete(),
              });
            } else {
              this.isRefreshing = false;
              observer.error(new Error("Token refresh failed"));
            }
          })
          .catch((error: unknown) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.error(error);
            observer.error(error);
          });
      });
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addTokenHeader(req, token as string))),
      );
    }
  }

  /**
   * Returns a cloned request with the Authorization header populated.
   */
  private addTokenHeader<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
    if (!this.shouldAttachAccessToken(req)) {
      return req;
    }

    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * Skips refresh handling for endpoints that already belong to auth flows,
   * preventing recursive refresh attempts and login request interference.
   */
  private shouldHandleUnauthorized<T>(req: HttpRequest<T>): boolean {
    if (this.isStaticAssetRequest(req)) {
      return false;
    }

    if (req.url.includes("/auth/refresh")) {
      return false;
    }

    if (req.url.includes("/auth/login")) {
      return false;
    }

    return true;
  }

  /**
   * Sends authenticated users to the in-shell forbidden page when the API denies access.
   */
  private shouldNavigateForbidden<T>(req: HttpRequest<T>): boolean {
    if (this.isStaticAssetRequest(req)) {
      return false;
    }

    if (!this.storage.getItem(StorageKeys.AccessToken)) {
      return false;
    }

    if (this.router.url.startsWith("/403") || this.router.url.startsWith("/forbidden")) {
      return false;
    }

    if (req.url.includes("/auth/")) {
      return false;
    }

    return true;
  }

  /**
   * Static files are served by the web host/storage layer. Sending API bearer
   * tokens to those URLs can break otherwise-public asset requests.
   */
  private shouldAttachAccessToken<T>(req: HttpRequest<T>): boolean {
    return !this.isStaticAssetRequest(req);
  }

  private isStaticAssetRequest<T>(req: HttpRequest<T>): boolean {
    const normalizedUrl = this.getPathname(req.url).replace(/^\/+/, "");
    return normalizedUrl.startsWith("Assets/");
  }

  private getPathname(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (exception) {
      void exception;
      return url.split("?", 1)[0];
    }
  }
}
